import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import restaurantService, {
  CatalogItem,
  CustomerDisplaySettings,
  MenuItem,
  Restaurant,
} from '../../services/restaurantService';
import cartService, { apiErrorMessage, CartOptions } from '../../services/cartService';
import type { CartActionResult, MenuCardAddToCartFlags } from '../../hooks/useMenuItemCartControls';
import Toast from '../Toast/Toast';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import CatalogIconRail from '../Catalog/CatalogIconRail';
import CatalogLandscapeBanner from '../Catalog/CatalogLandscapeBanner';
import MenuBrowseItemCard from './MenuBrowseItemCard';
import MenuViewSwitch from './MenuViewSwitch';
import { CART_UPDATED_EVENT } from '../../utils/cartEvents';
import { IconCart, IconCartFab, IconSearch } from '../RestaurantMenu/MenuListingIcons';
import MenuBrowseSkeleton from './MenuBrowseSkeleton';
import { useGuestOrderType } from '../../hooks/useGuestOrderType';
import { withGuestOrderTypeQuery } from '../../utils/guestOrderTypeStorage';
import { useGuestCanOrder } from '../../hooks/useGuestCanOrder';
import GuestOrderingBanner from '../common/GuestOrderingBanner/GuestOrderingBanner';
import { GuestOrderingStatus, isAcceptingGuestOrders } from '../../utils/guestOrdering';
import { useSsrPreload, matchSsrPreload } from '../../ssr/preload';
import SeoHead from '../Seo/SeoHead';
import { getRestaurantImageUrl } from '../../constants/images';
import './MenuBrowse.css';

function parseIdParam(value: string | null): number | null {
  if (!value) return null;
  const n = parseInt(value, 10);
  return Number.isFinite(n) ? n : null;
}

function toCatalogItem(
  entry: { id: number; type?: string; name?: string; iconUrl?: string; portraitBannerUrl?: string; landscapeBannerUrl?: string; displayOrder?: number },
  nameFallback?: string
): CatalogItem {
  return {
    id: entry.id,
    name: entry.name || entry.type || nameFallback || '',
    type: entry.type,
    iconUrl: entry.iconUrl,
    portraitBannerUrl: entry.portraitBannerUrl,
    landscapeBannerUrl: entry.landscapeBannerUrl,
    displayOrder: entry.displayOrder,
  };
}

const MenuBrowse: React.FC = () => {
  const { appName } = useParams<{ appName: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const guestOrderType = useGuestOrderType(appName);
  const { canOrder } = useGuestCanOrder(appName);
  const preload = useSsrPreload();
  const browsePreload = matchSsrPreload(preload, 'browse', appName) ? preload : null;

  const initialCuisineId = parseIdParam(searchParams.get('cuisine'));
  const initialCategoryId = parseIdParam(searchParams.get('category'));

  const [restaurant, setRestaurant] = useState<Restaurant | null>(browsePreload?.restaurant ?? null);
  const [guestOrdering, setGuestOrdering] = useState<GuestOrderingStatus | null>(browsePreload?.guestOrdering ?? null);
  const [displaySettings, setDisplaySettings] = useState<CustomerDisplaySettings | null>(
    browsePreload?.displaySettings ?? null
  );
  const [allItems, setAllItems] = useState<MenuItem[]>(browsePreload?.menuItems ?? []);
  const [categories, setCategories] = useState<CatalogItem[]>(browsePreload?.catalogCategories ?? []);
  const [cuisines, setCuisines] = useState<CatalogItem[]>(browsePreload?.catalogCuisines ?? []);
  const [loading, setLoading] = useState(!browsePreload?.menuItems);
  const [error, setError] = useState<string | null>(null);
  const [cuisineScopeId, setCuisineScopeId] = useState<number | null>(initialCuisineId);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(initialCategoryId);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [cartCount, setCartCount] = useState(0);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.body.classList.add('menu-browse-active');
    return () => document.body.classList.remove('menu-browse-active');
  }, []);

  const loadCartCount = useCallback(async () => {
    if (!appName) return;
    try {
      const response = await cartService.getCartCount(appName);
      if (response.success) {
        setCartCount(response.cartCount || 0);
      }
    } catch {
      /* ignore */
    }
  }, [appName]);

  useEffect(() => {
    if (!appName) return;
    let cancelled = false;

    const load = async () => {
      if (browsePreload?.menuItems && browsePreload.appName === appName) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const [response, restRes] = await Promise.all([
          restaurantService.getRestaurantMenu(appName),
          restaurantService.getRestaurant(appName),
        ]);
        if (cancelled) return;
        if (!response.success) {
          setError(response.message || 'Failed to load menu');
          return;
        }
        setRestaurant(response.restaurant);
        setGuestOrdering(restRes.guestOrdering ?? null);
        setDisplaySettings(response.displaySettings ?? restRes.displaySettings ?? null);
        setAllItems(response.menu?.menuList || []);
        setCategories(
          (response.categories || []).map((c) => toCatalogItem(c))
        );
        setCuisines(
          (response.cuisines || []).map((c) => toCatalogItem(c, c.name))
        );
      } catch (e: unknown) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load menu');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    loadCartCount();
    return () => {
      cancelled = true;
    };
  }, [appName, loadCartCount]);

  useEffect(() => {
    if (!appName) return undefined;
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ appName: string; count?: number }>).detail;
      if (detail?.appName !== appName) return;
      if (typeof detail.count === 'number') {
        setCartCount(detail.count);
        return;
      }
      loadCartCount();
    };
    window.addEventListener(CART_UPDATED_EVENT, handler);
    return () => window.removeEventListener(CART_UPDATED_EVENT, handler);
  }, [appName, loadCartCount]);

  const scopedItems = useMemo(() => {
    let items = allItems;
    if (cuisineScopeId != null) {
      items = items.filter((item) => item.cuisineId === cuisineScopeId);
    }
    if (selectedCategoryId != null) {
      items = items.filter((item) => item.categoryId === selectedCategoryId);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      items = items.filter((item) => item.item?.toLowerCase().includes(q));
    }
    return items;
  }, [allItems, cuisineScopeId, selectedCategoryId, searchQuery]);

  const railCategories: CatalogItem[] = useMemo(() => {
    const baseItems =
      cuisineScopeId != null
        ? allItems.filter((item) => item.cuisineId === cuisineScopeId)
        : allItems;
    const categoryIds = new Set(
      baseItems.map((item) => item.categoryId).filter((id): id is number => id != null)
    );
    return categories
      .filter((cat) => categoryIds.has(cat.id))
      .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
  }, [allItems, categories, cuisineScopeId]);

  const activeCategory = useMemo(
    () => categories.find((c) => c.id === selectedCategoryId) || null,
    [categories, selectedCategoryId]
  );

  const activeCuisine = useMemo(
    () => cuisines.find((c) => c.id === cuisineScopeId) || null,
    [cuisines, cuisineScopeId]
  );

  const pageTitle =
    activeCuisine?.name ||
    activeCategory?.name ||
    activeCategory?.type ||
    restaurant?.restaurantName ||
    'Menu';

  const formatPrice = (price: number) => {
    const symbol = restaurant?.currencySymbol || '₹';
    return `${symbol}${Math.round(price)}`;
  };

  const handleCategorySelect = (id: number | null) => {
    setSelectedCategoryId(id);
    contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleAddToCart = async (
    menuId: number,
    quantity: number,
    options?: CartOptions,
    itemQuantity?: number,
    flags?: MenuCardAddToCartFlags
  ): Promise<CartActionResult> => {
    // Shown inside the open options sheet instead (one error message, not two).
    const showError = (message: string) => {
      if (!flags?.quietErrors) setToastMessage(message);
    };
    if (!appName) return { success: false };
    if (!isAcceptingGuestOrders(guestOrdering)) {
      return { success: false };
    }
    try {
      const response = await cartService.addToCart(appName, menuId, quantity, guestOrderType, options);
      if (response.success) {
        setCartCount(response.cartCount || 0);
        setAllItems((prev) =>
          prev.map((item) => (item.id === menuId ? { ...item, qty: itemQuantity ?? quantity } : item))
        );
        return { success: true };
      }
      const message = response.message || 'Could not update your cart';
      showError(message);
      return { success: false, message };
    } catch (err: unknown) {
      const message = apiErrorMessage(err, 'Could not update your cart');
      showError(message);
      return { success: false, message };
    }
  };

  const handleBack = () => {
    if (appName) {
      navigate(`/restaurants/${appName}`);
    } else {
      navigate(-1);
    }
  };

  if (loading) {
    return <MenuBrowseSkeleton />;
  }

  if (error || !restaurant) {
    return (
      <div className="menu-browse-page">
        <Header />
        <div className="menu-browse-empty">
          <p>{error || 'Restaurant not found'}</p>
          <button type="button" onClick={handleBack}>
            Go back
          </button>
        </div>
        <Footer variant="restaurant" />
      </div>
    );
  }

  return (
    <div className="menu-browse-page">
      <SeoHead
        title={restaurant.restaurantName ? `${restaurant.restaurantName} menu` : 'Menu'}
        description={`Browse the menu at ${restaurant.restaurantName}.`}
        path={appName ? `/restaurants/${appName}/browse` : undefined}
        images={
          restaurant.coverImage
            ? [getRestaurantImageUrl(restaurant.id, restaurant.coverImage)]
            : undefined
        }
      />
      <Header />

      <header className="menu-browse-header">
        <button type="button" className="menu-browse-back" onClick={handleBack} aria-label="Back">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><polyline points="15 18 9 12 15 6" /></svg>
        </button>
        <div className="menu-browse-header-search">
          <IconSearch size={15} className="menu-browse-header-search__icon" />
          <input
            type="search"
            className="menu-browse-header-search__input"
            placeholder="Search dishes…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Search dishes"
          />
        </div>
        <div className="menu-browse-header-view-switch">
          <MenuViewSwitch
            appName={appName!}
            active="browse"
            cuisineId={cuisineScopeId}
            categoryId={selectedCategoryId}
          />
        </div>
      </header>
      <GuestOrderingBanner status={guestOrdering} compact />

      <div className="menu-browse-shell">
        <div className="menu-browse-body">
          <aside className="menu-browse-rail-wrap" aria-label="Categories">
            <CatalogIconRail
              items={railCategories}
              selectedId={selectedCategoryId}
              onSelect={handleCategorySelect}
            />
          </aside>

          <div className="menu-browse-content" ref={contentRef}>
          <CatalogLandscapeBanner
            item={activeCategory}
            title={activeCategory?.name || activeCategory?.type}
            subtitle={
              activeCuisine
                ? `${activeCuisine.name} · ${scopedItems.length} items`
                : scopedItems.length > 0
                  ? `${scopedItems.length} items`
                  : undefined
            }
          />

          {scopedItems.length === 0 ? (
            <div className="menu-browse-empty">
              <div className="menu-browse-empty__icon">
                <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2" />
                  <path d="M7 2v20" />
                  <path d="M21 15V2a5 5 0 0 0-5 5v6h3.5" />
                  <path d="M19.5 13V22" />
                </svg>
              </div>
              <p className="menu-browse-empty__title">Nothing here</p>
              <p className="menu-browse-empty__sub">No dishes found in this section.</p>
              <button
                type="button"
                className="menu-browse-empty__btn"
                onClick={() => navigate(`/restaurants/${appName}/menu`)}
              >
                View full menu
              </button>
            </div>
          ) : (
            <div className="menu-browse-grid">
              {scopedItems.map((item) => (
                <MenuBrowseItemCard
                  key={item.id}
                  item={item}
                  restaurant={restaurant}
                  formatPrice={formatPrice}
                  onAddToCart={handleAddToCart}
                  orderingEnabled={canOrder}
                  showImages={displaySettings?.showMenuItemImages === true}
                />
              ))}
            </div>
          )}
          </div>
        </div>
      </div>

      {canOrder && (
      <div className="menu-browse-mobile-fab">
        <Link
          to={withGuestOrderTypeQuery(`/restaurants/${appName}/cart`, appName, guestOrderType)}
          className="menu-browse-fab menu-browse-fab--cart"
          aria-label="Go to cart"
        >
          <IconCartFab />
          {cartCount > 0 && <span className="menu-browse-fab-badge">{cartCount}</span>}
        </Link>
      </div>
      )}

      {toastMessage && (
        <Toast message={toastMessage} type="error" onClose={() => setToastMessage(null)} />
      )}

      <Footer variant="restaurant" />
    </div>
  );
};

export default MenuBrowse;
